import React, {
  forwardRef,
  useRef,
  useImperativeHandle,
  useState,
  useMemo,
} from "react";
import { Select, Tag } from "antd";
import type { SelectProps } from "antd";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import "./CustomSelect.scss";

export type CustomSelectHandle = {
  focus: () => void;
};

export interface CustomSelectProps<ValueType = any>
  extends SelectProps<ValueType> {
  id?: string;
  className?: string;
  icon?: React.ReactNode;
  containerClassName?: string;
  displayLabel?: React.ReactNode;
  alwaysShowDisplayLabel?: boolean;
  treatValuesAsAll?: ValueType[];
  showSelectedAsTags?: boolean; // nova prop: renderizar valores selecionados como tags
  tagClosable?: boolean; // nova prop: exibir botão de fechar nas tags
}

const CustomSelect = forwardRef<CustomSelectHandle, CustomSelectProps>(
  function CustomSelect(
    {
      id,
      className,
      icon,
      containerClassName,
      suffixIcon,
      value,
      onDropdownVisibleChange,
      displayLabel,
      alwaysShowDisplayLabel = false,
      treatValuesAsAll = [] as any,
      mode,
      showSelectedAsTags = false,
      tagClosable = true,
      ...rest
    },
    ref
  ) {
    const selectRef = useRef<any>(null);
    // track dropdown visibility for icon and container styling but do NOT control Select open state
    const [visible, setVisible] = useState<boolean>(false);

    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          selectRef.current?.focus?.();
        },
      }),
      []
    );

    const hasValue = useMemo(() => {
      if (mode) {
        // multi or tags mode rely on array length
        if (Array.isArray(value)) return value.length > 0;
        return false;
      }
      return value !== undefined && value !== null && value !== "";
    }, [value, mode]);

    const isAllEquivalent = useMemo(() => {
      if (!hasValue) return false;
      if (!treatValuesAsAll || !treatValuesAsAll.length) return false;
      return treatValuesAsAll.includes(value as any);
    }, [hasValue, value, treatValuesAsAll]);

    const showSelectedText =
      hasValue && !alwaysShowDisplayLabel && !isAllEquivalent;

    const selectedLabel: React.ReactNode = useMemo(() => {
      if (!showSelectedText) return null;
      if (mode && Array.isArray(value)) {
        if (Array.isArray(rest.options)) {
          const labels = (value as any[]).map((val) => {
            const found = (rest.options as any[]).find(
              (opt) => opt?.value === val
            );
            return found?.label ?? found?.value ?? val;
          });
          return labels.join(", ");
        }
        return (value as any[]).join(", ");
      }
      if (Array.isArray(rest.options)) {
        const found = (rest.options as any[]).find(
          (opt) => opt?.value === value
        );
        return found?.label ?? found?.value ?? value;
      }
      return value as any;
    }, [showSelectedText, rest.options, value, mode]);

    const displayText = showSelectedText
      ? selectedLabel
      : displayLabel ?? rest.placeholder ?? "";

    const handleVisibleChange = (v: boolean) => {
      setVisible(v);
      onDropdownVisibleChange?.(v);
    };

    // Handler para remover uma tag específica (apenas para modo múltiplo/tags)
    const handleRemoveTag = (val: any, e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (!Array.isArray(value)) return;
      const newValues = (value as any[]).filter((v) => v !== val);
      // chama o onChange do Select para manter comportamento controlado
      (rest.onChange as any)?.(newValues, undefined, undefined);
      // mantém foco no select
      selectRef.current?.focus?.();
    };

    const showTagsDisplay =
      showSelectedAsTags &&
      mode &&
      Array.isArray(value) &&
      (value as any[]).length > 0;

    return (
      <div id={id} className={`custom-select ${className || ""}`.trim()}>
        <div
          className={`custom-select-container ${visible ? "open" : ""} ${
            hasValue && !isAllEquivalent ? "has-value" : ""
          } ${containerClassName || ""}`.trim()}
        >
          <span className="cs-display">
            {icon ? <span className="cs-icon">{icon}</span> : null}

            {showTagsDisplay ? (
              <div
                className="cs-tags"
                // onMouseDown={(e) => e.stopPropagation()}
              >
                {(value as any[]).map((val) => {
                  const found = Array.isArray(rest.options)
                    ? (rest.options as any[]).find((opt) => opt?.value === val)
                    : undefined;
                  const label = found?.label ?? found?.value ?? val;
                  return (
                    <Tag
                      className="cs-tag"
                      key={String(val)}
                      closable={tagClosable}
                      onClose={(e) => handleRemoveTag(val, e)}
                    >
                      <span className="cs-tag-label">{label}</span>
                    </Tag>
                  );
                })}
              </div>
            ) : (
              <span className="cs-text">{displayText}</span>
            )}
          </span>
          <Select
            ref={selectRef}
            value={value}
            {...rest}
            placeholder={displayLabel as any}
            getPopupContainer={(node) => node?.parentElement || document.body}
            onDropdownVisibleChange={handleVisibleChange}
            suffixIcon={
              suffixIcon !== undefined ? (
                suffixIcon
              ) : visible ? (
                <UpOutlined />
              ) : (
                <DownOutlined />
              )
            }
            mode={mode}
            variant="borderless"
            className={`cs-ant-select cs-overlay-hidden`.trim()}
          />
        </div>
      </div>
    );
  }
);

CustomSelect.displayName = "CustomSelect";

export default CustomSelect;
